import React, { useState } from "react";
import { Informations, Interest } from "../types";
import ThemeToggle from "./ThemeToggle";
import ProfileSelector from "./ProfileSelector";

type Props = {
  informations: Informations;
  interests?: Interest[];
};

export default function Sidebar({ informations, interests }: Props) {
  const isAvailable = !!informations.disponible;
  const [expandedContact, setExpandedContact] = useState<number | null>(null);

  // Parse experience number for emphasis
  const expMatch = informations.experience.match(/(\+?\d+)/);
  const expNumber = expMatch ? expMatch[1] : null;
  const expRest = expNumber
    ? informations.experience.replace(expNumber, "").trim()
    : informations.experience;

  return (
    <div className="space-y-5">
      {/* Photo + theme toggle */}
      <div className="relative">
        <div className="flex justify-center lg:justify-start">
          <div className="w-28 h-28 lg:w-full lg:h-auto lg:aspect-square lg:max-w-[220px] rounded-3xl overflow-hidden ring-1 ring-neutral-200 dark:ring-white/10 shadow-2xl shadow-neutral-300/40 dark:shadow-black/50">
            <img
              src="./profil-image.jpg"
              alt={informations.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="no-print absolute top-0 right-0">
          <ThemeToggle />
        </div>
      </div>

      {/* Identity */}
      <div className="text-center lg:text-left space-y-3">
        <h1 className="font-display text-3xl lg:text-4xl tracking-tight leading-none">
          {informations.name}
        </h1>

        <div className="flex items-center justify-center lg:justify-start gap-3 flex-wrap">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider
              ${
                isAvailable
                  ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-500/20"
                  : "bg-neutral-100 dark:bg-white/5 text-neutral-400 ring-1 ring-neutral-200 dark:ring-white/10"
              }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isAvailable ? "bg-emerald-500 animate-pulse" : "bg-neutral-400"
              }`}
            />
            Freelance
            {isAvailable
              ? ` \u2014 Dispo. ${informations.disponible}`
              : " \u2014 Indisponible"}
          </span>
        </div>

        {/* Experience highlight */}
        {expNumber ? (
          <div className="flex items-baseline gap-1.5 justify-center lg:justify-start">
            <span className="text-4xl font-display font-bold text-neutral-900 dark:text-neutral-100 leading-none">
              {expNumber}
            </span>
            <span className="text-xs font-bold uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
              {expRest}
            </span>
          </div>
        ) : (
          <p className="text-xs font-bold tracking-wide uppercase text-neutral-900 dark:text-neutral-100">
            {informations.experience}
          </p>
        )}
      </div>

      {/* Profile selector */}
      <div className="no-print">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-2">
          Profil
        </p>
        <ProfileSelector layout="vertical" />
      </div>

      {/* Separator */}
      <div className="h-px bg-neutral-200 dark:bg-white/[0.06]" />

      {/* Contact icons */}
      <div className="flex flex-col items-center lg:items-start gap-3">
        <div className="flex gap-2 justify-center lg:justify-start flex-wrap">
          {informations.contact
            .filter((c) => c.name !== "Full Remote" && !c.name.includes("Permis") && !c.name.includes("sylvaintoffoli"))
            .map((c, i) => {
              const isDirectLink = c.link && c.imageBrands;
              const isOpen = expandedContact === i;

              if (isDirectLink) {
                return (
                  <a
                    key={i}
                    href={c.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-[13px] transition-all duration-300
                      bg-neutral-100 dark:bg-white/[0.06] text-neutral-400 dark:text-neutral-500 hover:bg-neutral-900 hover:text-white dark:hover:bg-white dark:hover:text-neutral-900 hover:scale-110 hover:shadow-lg hover:shadow-neutral-900/20 dark:hover:shadow-white/10"
                    title={c.name}
                  >
                    {c.image && (
                      <i className={`fa-brands ${c.image}`} />
                    )}
                  </a>
                );
              }

              return (
                <button
                  key={i}
                  onClick={() => setExpandedContact(isOpen ? null : i)}
                  className={`w-9 h-9 rounded-xl flex items-center justify-center text-[13px] transition-all duration-300
                    ${
                      isOpen
                        ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900 scale-110 shadow-lg shadow-neutral-900/20 dark:shadow-white/10"
                        : "bg-neutral-100 dark:bg-white/[0.06] text-neutral-400 dark:text-neutral-500 hover:bg-neutral-200 dark:hover:bg-white/10 hover:scale-105"
                    }`}
                  title={c.name}
                >
                  {c.image && (
                    <i className={`fa${c.imageBrands ? "-brands" : ""} ${c.image}`} />
                  )}
                  {c.imageUrl && (
                    <img src={c.imageUrl} className="w-3.5 h-3.5" alt="" />
                  )}
                </button>
              );
            })}
        </div>
        {/* Expanded detail */}
        {(() => {
          const filtered = informations.contact.filter(
            (c) => c.name !== "Full Remote" && !c.name.includes("Permis") && !c.name.includes("sylvaintoffoli")
          );
          const contact = expandedContact !== null ? filtered[expandedContact] : null;
          const isDirectLink = contact?.link && contact?.imageBrands;
          if (!contact || isDirectLink) return null;
          return (
            <div className="text-center lg:text-left overflow-hidden transition-all duration-300 max-h-10 opacity-100">
              {contact.link ? (
                <a
                  href={contact.link}
                  className="text-xs font-medium text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white transition-colors"
                >
                  {contact.name}
                </a>
              ) : (
                <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
                  {contact.name}
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {/* Interests */}
      {interests && interests.length > 0 && (
        <>
          <div className="h-px bg-neutral-200 dark:bg-white/[0.06]" />
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-3">
              Centres d'intérêt
            </p>
            <div className="flex flex-wrap gap-1.5">
              {interests.map((interest, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-neutral-100 dark:bg-white/[0.06] text-xs text-neutral-500 dark:text-neutral-400"
                >
                  {interest.pictureUrl ? (
                    <img
                      src={interest.pictureUrl}
                      alt=""
                      className="w-3 h-3 opacity-50"
                    />
                  ) : (
                    <i className={`fa ${interest.picture} text-[10px] opacity-50`} />
                  )}
                  {interest.name}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Download */}
      <button
        onClick={() => window.print()}
        className="no-print w-full flex items-center justify-center gap-2 px-4 py-2.5
                   rounded-xl bg-neutral-900 dark:bg-white text-white dark:text-neutral-900
                   text-sm font-semibold hover:opacity-80 transition-opacity"
      >
        <i className="fa fa-arrow-down text-[10px]" />
        Télécharger PDF
      </button>
    </div>
  );
}
